<template>
    <div class="order-form">
        <keep-alive>
            <component v-bind:is="orderFormPage1"
                v-if="currentForm === 'page-1'"
                :customers="customers"
                :subcustomer="subcustomer"
                :customer-label="customerLabel"
                :selected-customer="lSelectedCustomer"
                :delivery-date="lDeliveryDate"
                :end-preparation-date="lEndPreparationDate"
                :can-select-red-dates="canSelectRedDates"
                :is-recurrent="isRecurrent"
                :order="order"
                :authorization-token-type="authorizationTokenType"
                :back-url="backUrl"
                @submit="onSubmitPage1"
            />
            <CartOrderForm
                v-if="currentForm === 'page-2'"
                :customer="lSelectedCustomer"
                :subcustomer="subcustomer"
                :customer-label="customerLabel"
                :ordered-products="orderedProducts"
                :delivery-date="lDeliveryDate"
                :end-preparation-date="lEndPreparationDate"
                :is-recurrent="isRecurrent"
                :order="order"
                :alerts-action="alertsAction"
                :products-action="productsAction"
                :previous-orders-action="previousOrdersAction"
                :block-not-orderable="blockNotOrderable"
                :block-not-authorized="isRecurrent"
                @back="onClickBackPage2"
                @submit="onSubmitPage2"
            />
        </keep-alive>
        <component v-bind:is="orderFormPage3"
            v-if="currentForm === 'page-3'"
            :customer="lSelectedCustomer"
            :subcustomer="subcustomer"
            :customer-label="customerLabel"
            :ordered-products="orderedProducts"
            :prefill-comment="comment"
            :precheck-comment-public="commentPublic"
            :precheck-comment-production-slip="commentProductionSlip"
            :delivery-date="lDeliveryDate"
            :end-preparation-date="lEndPreparationDate"
            :is-recurrent="isRecurrent"
            :order="order"
            :categoriesMainCategory="categoriesMainCategory"
            :categoriesById="categoriesById"
            :loading="orderPrecheckLoading"
            :sending="sending"
            @back="onClickBackPage3"
            @submit="onSubmitLastPage"
            @mounted="onLastPageMounted"
        >
            <template #alerts>
                <AlertsZone
                    v-if="warningMessages.length"
                    type="warning"
                    title="Attention : validation requise"
                    :messages="warningMessages"
                />
                <AlertsZone
                    v-if="dangerMessages.length"
                    type="danger"
                    title="Attention : validation requise"
                    :messages="dangerMessages"
                />
            </template>
            <template #errors>
                <errors-zone :errors="errors"></errors-zone>
                <errors-zone :errors="orderPrecheckErrors"></errors-zone>
            </template>
            <template #submitButton="sp">
                <button v-if="orderPrecheckLoading" type="button" class="btn btn-secondary float-right" disabled>
                    <i class="fas fa-spinner fa-spin"></i> Vérification en cours...
                </button>
                <template v-else-if="orderPrecheckLoaded">
                    <template v-if="needFinalAuthorizationToken">
                        <AuthorizationModal
                            :token-type="authorizationTokenType"
                            :token-payload="finalAuthorizationTokenPayload"
                            @authorised="onFinalAuthorizationModalAuthorize(...arguments, sp.onClickNext)"
                        >
                            <template #button="sp">
                                <AuthorizationModalButton
                                    :class="dangerZoneDisplayed ? 'btn-danger' : 'btn-warning'"
                                    :disabled="!canClickSubmit"
                                    @click="sp.showModal()"
                                >
                                    <template v-if="sending">
                                        <i class="fad fa-fw fa-spinner fa-spin"></i>
                                        {{ order ? 'Modification de la commande' : 'Création de la commande' }}
                                    </template>
                                    <template v-else>
                                        <i class="fad fa-fw fa-cart-plus"></i>
                                        {{ order ? 'Modifier la commande' : 'Créer la commande' }}
                                        {{ isRecurrent ? 'hebdomadaire' : ''}}
                                    </template>
                                </AuthorizationModalButton>
                            </template>
                            <template #messages>
                                <AlertsZone
                                    v-if="warningZoneDisplayed"
                                    type="warning"
                                    title="Attention : validation requise"
                                    :messages="warningMessages"
                                />
                                <AlertsZone
                                    v-if="dangerZoneDisplayed"
                                    type="danger"
                                    title="Attention : validation requise"
                                    :messages="dangerMessages"
                                />
                            </template>
                        </AuthorizationModal>
                    </template>
                    <button
                        v-else
                        ref="submitBtn"
                        class="btn btn-success float-right"
                        type="button"
                        :disabled="!canClickSubmit"
                        @click="sp.onClickNext"
                    >
                        <template v-if="sending">
                            <i class="fad fa-fw fa-spinner fa-spin"></i>
                            {{ order ? 'Modification de la commande' : 'Création de la commande' }}
                        </template>
                        <template v-else>
                            <i class="fad fa-fw fa-cart-plus"></i>
                            {{ order ? 'Modifier la commande' : 'Créer la commande' }}
                            {{ isRecurrent ? 'hebdomadaire' : ''}}
                        </template>
                    </button>
                </template>
            </template>
        </component>
    </div>
</template>

<script>
import AlertsZone from "./AlertsZone"
import AuthorizationModal from "./AuthorizationModal"
import AuthorizationModalButton from "./AuthorizationModalButton"
import CartOrderForm from "./CartOrderForm"
import ErrorsZone from "./ErrorsZone"

import {momentDay} from "../moment-with-helpers"
import {usePostRequest} from "./compositions/axios/postRequest"
import {usePatchRequest} from "./compositions/axios/patchRequest"
import {dangerZoneDisplayed, warningZoneDisplayed} from "./compositions/OrderWarnings"

export default {
    components: {
        AlertsZone,
        ErrorsZone,
        CartOrderForm,
        AuthorizationModal,
        AuthorizationModalButton,
    },

    props: {
        categoriesMainCategory: {
            type: Object,
            required: true,
        },
        categoriesById: {
            type: Object,
            required: true,
        },
        order: {
            type: Object,
            required: false,
        },
        customers: {
            type: Array,
            required: true,
        },
        action: {
            type: String,
            required: true,
        },
        deliveryDate: {
            type: Object,
            required: false,
        },
        endPreparationDate: {
            type: Object,
            required: false,
        },
        canSelectRedDates: {
            type: Boolean,
            required: true,
        },
        isRecurrent: {
            type: Boolean,
            required: false,
            default: false,
        },
        selectedCustomer: {
            type: Object,
            required: false,
        },
        customerLabel: {
            type: String,
            required: true,
        },
        authorizationTokenType: {
            type: String,
            required: true,
        },
        formType: {
            type: String,
            required: true,
        },
        alertsAction: {
            type: String,
            required: false,
        },
        productsAction: {
            type: String,
            required: false,
        },
        previousOrdersAction: {
            type: String,
            required: false,
        },
        blockNotOrderable: {
            type: Boolean,
            required: false,
        },
        backUrl: {
            type: String,
            required: false,
        },
    },

    setup(props) {
        let useMethod = usePostRequest()
        let useMethodPrecheck = usePostRequest()
        if (props && props.order) {
            useMethod = usePatchRequest()
            useMethodPrecheck = usePatchRequest()
        }

        const orderPrecheckLoading = useMethodPrecheck.loading
        const orderPrecheckLoaded = useMethodPrecheck.loaded
        const orderPrecheckErrors = useMethodPrecheck.errors

        const sending = useMethod.loading;
        const errors = useMethod.errors;

        let send = useMethod.post
        let orderPrecheckSend = useMethodPrecheck.post
        if (props && props.order) {
            send = useMethod.patch
            orderPrecheckSend = useMethodPrecheck.patch
        }

        return {
            orderPrecheckLoading,
            orderPrecheckLoaded,
            orderPrecheckErrors,
            orderPrecheckSend,
            sending,
            errors,
            send,
        };
    },

    data() {
        let common = {
            currentForm: 'page-1',
            authorizationToken: null,
            orderFormPage1: 'store-contact-order-form-page-1',
            orderFormPage3: 'store-contact-order-form-page-3',
            alerts: null,
            lastPrecheckResponse: null,
            finalAuthorizationToken: null,
            comment: "",
            commentPublic: false,
            commentProductionSlip: false,
        };

        if (this.formType === 'customer-list') {
            common['orderFormPage1'] = 'customer-list-order-form-page-1';
            common['orderFormPage3'] = 'customer-list-order-form-page-3';
        } else if (this.formType === 'not-invoiced') {
            common['orderFormPage1'] = 'not-invoiced-order-form-page-1';
            common['orderFormPage3'] = 'not-invoiced-order-form-page-3';
        }

        if (this.order) {
            return {
                ...common,
                lSelectedCustomer: this.order.customer,
                lDeliveryDate: {
                    date: momentDay(this.order.delivery_date).toDate(),
                    ...this.order.delivery_date_authorizations
                },
                lEndPreparationDate: {
                    date: momentDay(this.order.end_preparation_date).toDate(),
                    permission: this.order.end_preparation_date_permission
                },
                orderedProducts: this.order.ordered_products_by_sku,
                subcustomer: this.order.subcustomer,
            }
        } else {
            return {
                ...common,
                lSelectedCustomer: this.selectedCustomer || null,
                lDeliveryDate: this.deliveryDate || null,
                lEndPreparationDate: (
                    this.endPreparationDate && {
                        date: momentDay(this.endPreparationDate.date).toDate(),
                        permission: this.endPreparationDate.permission
                    }) || null,
                authorizationToken: null,
                orderedProducts: null,
                subcustomer: {
                    meta: {
                        phone: '',
                        email: '',
                    },
                    name: '',
                },
            }
        }
    },

    created() {
        window.addEventListener('beforeunload', this.showQuitAlert);
    },

    beforeDestroy() {
        window.removeEventListener('beforeunload', this.showQuitAlert);
    },

    computed: {
        canClickSubmit() {
            return this.orderPrecheckLoaded
                && !this.sending
        },

        finalAuthorizationTokenPayload() {
            let orderedProductsArray = [];
            for (const sku in this.orderedProducts) {
                orderedProductsArray.push({
                    sku,
                    quantity: this.orderedProducts[sku].nb_ordered,
                });
            }

            return {
                order_id: (this.order && this.order.id) || null,
                token_type: this.authorizationTokenType,
                customer_type: this.lSelectedCustomer.type,
                customer_id: this.lSelectedCustomer.id,
                subcustomer_name: (this.subcustomer && this.subcustomer.name) || null,
                subcustomer_id: (this.order && this.order.fk_subcustomer) || null,
                delivery_date: momentDay(this.lDeliveryDate.date).format('YYYY-MM-DD'),
                end_preparation_date: momentDay(this.lEndPreparationDate.date).format('YYYY-MM-DD'),
                paid_at: (this.order && this.order.paid_at && momentDay(this.order.paid_at).format('YYYY-MM-DD')) || null,
                products: orderedProductsArray,
            };
        },

        needFinalAuthorizationToken() {
            return !this.finalAuthorizationToken && (this.dangerZoneDisplayed || this.warningZoneDisplayed);
        },

        previousTokenAuthorizations() {
            if (this.authorizationToken && this.authorizationToken.hasOwnProperty('authorizations')) {
                return this.authorizationToken.authorizations
            }
            return null
        },

        warningMessages() {
            return this.authorizationsMessages('restricted')
        },

        dangerMessages() {
            return this.authorizationsMessages('forbidden')
        },

        warningZoneDisplayed,

        dangerZoneDisplayed,
    },

    methods: {
        authorizationsMessages(warnLevel) {
            let messages = []

            if (this.lastPrecheckResponse && this.lastPrecheckResponse.hasOwnProperty('authorizations')) {
                for (const authorization of this.lastPrecheckResponse.authorizations) {
                    if (authorization.warnLevel !== warnLevel) {
                        continue
                    }

                    if (this.previousTokenAuthorizations && this.previousTokenAuthorizations[authorization.permission]) {
                        continue
                    }

                    messages.push(authorization.reason);
                }
            }
            return messages
        },

        onFinalAuthorizationModalAuthorize(token, callback) {
            this.finalAuthorizationToken = token

            callback()
        },

        showQuitAlert(e) {
            // Cancel the event
            e.preventDefault();
            // Chrome requires returnValue to be set
            e.returnValue = '';
        },

        onSubmitPage1(data) {
            this.lSelectedCustomer = data.selectedCustomer;
            this.lDeliveryDate = data.deliveryDate;
            this.lEndPreparationDate = data.endPreparationDate;
            this.authorizationToken = data.authorizationToken;
            this.subcustomer = data.subcustomer;

            if (this.order && this.order.paid_at && this.order.final_customer_type === 'pickup_point_subcustomer') {
                this.currentForm = 'page-3';
            } else {
                this.currentForm = 'page-2';
            }
        },

        onClickBackPage2() {
            this.currentForm = 'page-1';
        },

        onSubmitPage2(pageData) {
            this.orderedProducts = pageData.cartProducts;
            this.setCommentProperties(pageData);
            this.currentForm = 'page-3';
        },

        onClickBackPage3(pageData) {
            this.setCommentProperties(pageData);
            if (this.order && this.order.paid_at && this.order.final_customer_type === 'pickup_point_subcustomer') {
                this.currentForm = 'page-1';
            } else {
                this.currentForm = 'page-2';
            }
        },

        async onLastPageMounted() {
            let orderedProductsArray = [];
            for (const sku in this.orderedProducts) {
                orderedProductsArray.push({
                    sku,
                    quantity: this.orderedProducts[sku].nb_ordered,
                });
            }

            this.lastPrecheckResponse = await this.orderPrecheckSend(this.action + '?precheck=true', {
                customer_type: this.lSelectedCustomer.type,
                customer_id: this.lSelectedCustomer.id,
                delivery_date: momentDay(this.lDeliveryDate.date).format('YYYY-MM-DD'),
                end_preparation_date: this.lEndPreparationDate ? momentDay(this.lEndPreparationDate.date).format('YYYY-MM-DD') : null,
                authorization_token: this.authorizationToken ? this.authorizationToken.token : null,
                products: orderedProductsArray,
                subcustomer: this.subcustomer,
            });
        },

        async onSubmitLastPage(data) {
            let orderedProductsArray = [];
            for (const sku in this.orderedProducts) {
                orderedProductsArray.push({
                    sku,
                    quantity: this.orderedProducts[sku].nb_ordered,
                });
            }

            const tokenToSend = this.finalAuthorizationToken || this.authorizationToken

            let response = await this.send(this.action, {
                customer_type: this.lSelectedCustomer.type,
                customer_id: this.lSelectedCustomer.id,
                delivery_date: momentDay(this.lDeliveryDate.date).format('YYYY-MM-DD'),
                end_preparation_date: this.lEndPreparationDate ? momentDay(this.lEndPreparationDate.date).format('YYYY-MM-DD') : null,
                authorization_token: tokenToSend && tokenToSend.token,
                products: orderedProductsArray,
                comment: data.comment || '',
                comment_public: data.commentPublic || false,
                comment_production_slip: data.commentProductionSlip || false,
                subcustomer: this.subcustomer,
            });

            if (response && response.success) {
                window.removeEventListener('beforeunload', this.showQuitAlert);
                if (response.redirect) {
                    window.location = response.redirect;
                }
            } else {
                this.finalAuthorizationToken = null
            }
        },

        setCommentProperties(pageData) {
            if (!pageData) {
                return;
            }
            if (pageData.comment !== undefined && pageData.comment !== null) {
                this.comment = pageData.comment;
            }
            if (pageData.commentPublic !== undefined && pageData.commentPublic !== null) {
                this.commentPublic = pageData.commentPublic;
            }
            if (pageData.commentProductionSlip !== undefined && pageData.commentProductionSlip !== null) {
                this.commentProductionSlip = pageData.commentProductionSlip;
            }
        },
    },
}
</script>
